// AI Service - Real API integrations

// Generate ad creative using AI
export const generateImage = async (prompt, options = {}) => {
    try {
        const response = await fetch('http://localhost:3001/api/generate-creative', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                prompt,
                brand: options.brand,
                format: options.format || 'social-post',
                platform: options.platform || 'instagram',
                selectedImages: options.selectedImages || [],
                enhancedImages: options.enhancedImages || {},
                editedImages: options.editedImages || {},
            }),
        });

        // Check if response is HTML (server not responding correctly)
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('text/html')) {
            throw new Error('Server is not running or not responding correctly. Please run: npm run server');
        }

        if (!response.ok) {
            let errorMessage = 'Creative generation failed';
            try {
                const error = await response.json();
                errorMessage = error.details || error.error || errorMessage;
            } catch (e) {
                // If we can't parse the error response, use the default message
            }
            throw new Error(errorMessage);
        }

        const data = await response.json();

        if (options.onTokenUsage && data.usageMetadata) {
            const totalTokens = data.usageMetadata.totalTokenCount;
            if (totalTokens) {
                options.onTokenUsage(totalTokens);
            }
        }

        // Validate response structure
        if (!data.visual || !data.copy || !data.htmlTemplate) {
            throw new Error('Invalid response from server - missing required fields');
        }

        // Return the creative data with a preview URL (we'll generate a data URL from the HTML)
        return {
            visual: data.visual,
            copy: data.copy,
            design: data.design,
            htmlTemplate: data.htmlTemplate,
            prompt: data.prompt,
            generatedAt: data.generatedAt,
            // Create a preview image from the HTML template
            url: createPreviewFromHTML(data.htmlTemplate, data.copy),
        };
    } catch (error) {
        console.error('Creative generation error:', error);

        // Provide more helpful error messages
        if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
            throw new Error('Cannot connect to server. Make sure to run: npm run server');
        }

        throw new Error(error.message);
    }
};

// Helper function to create a preview representation
function createPreviewFromHTML(htmlTemplate, copy) {
    // For now, return a placeholder that indicates this is AI-generated
    // In a real implementation, you might want to render the HTML to an image
    // or use a screenshot service
    return `data:text/html;charset=utf-8,${encodeURIComponent(htmlTemplate)}`;
}

// Simulate AI video generation
export const generateVideo = async (prompt, options = {}) => {
    await new Promise(resolve => setTimeout(resolve, 4000));

    return {
        url: '#', // Mock video URL
        thumbnail: `https://picsum.photos/seed/${Date.now()}/800/450`,
        prompt,
        duration: options.duration || 15,
        generatedAt: new Date().toISOString(),
    };
};

// Generate AI ad copy
export const generateAdCopy = async (context) => {
    await new Promise(resolve => setTimeout(resolve, 1500));

    const { brand, objective, platform, keyMessage } = context;

    // Mock generated copy variations
    const variations = [
        {
            headline: `${keyMessage} - ${brand}`,
            body: `Discover how ${brand} is transforming ${objective.toLowerCase()}. Join thousands of satisfied customers.`,
            cta: 'Learn More',
        },
        {
            headline: `The Future of ${objective}`,
            body: `${brand} makes it simple. ${keyMessage} Start your journey today.`,
            cta: 'Get Started',
        },
        {
            headline: `${keyMessage}`,
            body: `See why leading companies choose ${brand}. Experience the difference.`,
            cta: 'Try Now',
        },
    ];

    return variations;
};

// Build prompt from wizard inputs
export const buildPrompt = (inputs) => {
    const { objective, keyMessage, visualStyle, brandVoice, constraints, platform, brand } = inputs;

    let prompt = '';

    // Add objective context
    if (objective) {
        prompt += `Create a ${objective.toLowerCase()} advertisement `;
    }

    // Add key message
    if (keyMessage) {
        prompt += `focusing on "${keyMessage}". `;
    } else if (brand?.voice?.keyMessages && brand.voice.keyMessages.length > 0) {
        // Use brand's key messages if no custom message provided
        prompt += `highlighting ${brand.voice.keyMessages[0]}. `;
    }

    // Add brand-specific information
    if (brand) {
        // Add industry context
        if (brand.industry) {
            prompt += `Industry: ${brand.industry}. `;
        }

        // Add products/services if available
        if (brand.products) {
            prompt += `Products/Services: ${brand.products}. `;
        }

        // Add target audience information
        if (brand.audience) {
            if (brand.audience.demographics) {
                prompt += `Target audience: ${brand.audience.demographics}. `;
            }
            if (brand.audience.interests && Array.isArray(brand.audience.interests)) {
                prompt += `Audience interests: ${brand.audience.interests.join(', ')}. `;
            }
            if (brand.audience.painPoints && Array.isArray(brand.audience.painPoints)) {
                prompt += `Address pain points: ${brand.audience.painPoints.join(', ')}. `;
            }
        }

        // Add brand personality
        if (brand.voice?.personality) {
            prompt += `Brand personality: ${brand.voice.personality}. `;
        }

        // Add brand guidelines
        if (brand.guidelines) {
            prompt += `Brand guidelines: ${brand.guidelines}. `;
        }

        // Add visual identity
        if (brand.visual) {
            const colors = [];
            if (brand.visual.primaryColor) colors.push(`primary ${brand.visual.primaryColor}`);
            if (brand.visual.secondaryColor) colors.push(`secondary ${brand.visual.secondaryColor}`);
            if (brand.visual.accentColor) colors.push(`accent ${brand.visual.accentColor}`);

            if (colors.length > 0) {
                prompt += `Brand colors: ${colors.join(', ')}. `;
            }

            if (brand.visual.fonts) {
                prompt += `Typography: ${brand.visual.fonts.heading || 'modern'} for headings, ${brand.visual.fonts.body || 'clean sans-serif'} for body. `;
            }
        }

        // Also handle colors in the alternate format (colors.primary instead of visual.primaryColor)
        if (brand.colors) {
            const colors = [];
            if (brand.colors.primary) colors.push(`primary ${brand.colors.primary}`);
            if (brand.colors.secondary) colors.push(`secondary ${brand.colors.secondary}`);
            if (brand.colors.accent) colors.push(`accent ${brand.colors.accent}`);

            if (colors.length > 0) {
                prompt += `Brand colors: ${colors.join(', ')}. `;
            }
        }
    }

    // Add visual style
    if (visualStyle) {
        prompt += `Visual style: ${visualStyle}. `;
    }

    // Add brand voice
    if (brandVoice) {
        prompt += `Tone: ${brandVoice}. `;
    }

    // Add platform-specific requirements
    if (platform) {
        const platformSpecs = {
            instagram: 'Optimized for Instagram feed, 1:1 ratio, vibrant colors',
            facebook: 'Optimized for Facebook feed, 1.91:1 ratio, engaging visuals',
            linkedin: 'Professional aesthetic for LinkedIn, 1.91:1 ratio, business-focused',
            tiktok: 'Dynamic, vertical video format, 9:16 ratio, attention-grabbing',
            google: 'Clean, informative for display ads, multiple sizes',
        };
        prompt += platformSpecs[platform] + '. ';
    }

    // Add constraints
    if (constraints && constraints.length > 0) {
        prompt += `Constraints: ${constraints.join(', ')}. `;
    }

    return prompt.trim();
};

// Suggest prompts based on brand and objectives
export const suggestPrompts = (brand, objective) => {
    const suggestions = {
        awareness: [
            `Eye-catching hero image showcasing ${brand.name}'s innovative solution with modern design`,
            `Lifestyle shot of happy customers using ${brand.name} products in authentic settings`,
            `Bold, minimalist design with ${brand.name} logo and key benefit statement`,
        ],
        conversion: [
            `Product demonstration showing clear before/after results with ${brand.name}`,
            `Limited-time offer banner with urgency elements and clear call-to-action`,
            `Customer testimonial visual with 5-star rating and compelling quote`,
        ],
        engagement: [
            `Interactive quiz or poll format encouraging audience participation`,
            `Behind-the-scenes content showing ${brand.name}'s team and culture`,
            `User-generated content showcase featuring real customer stories`,
        ],
    };

    return suggestions[objective.toLowerCase()] || [];
};

// Simulate extracting brand info from a URL (Shopify, WordPress, Socials)
export const extractBrandFromSource = async (url, type, onTokenUsage) => {
    try {
        // Try to call the local analysis server
        const response = await fetch(`http://localhost:3001/api/analyze?url=${encodeURIComponent(url)}`);
        if (response.ok) {
            const data = await response.json();

            if (onTokenUsage && data.usageMetadata) {
                const totalTokens = data.usageMetadata.totalTokenCount;
                if (totalTokens) {
                    onTokenUsage(totalTokens);
                }
            }

            return {
                colors: data.colors,
                voice: data.voice,
                logo: data.logo,
                industry: data.industry,
                products: data.products,
                name: data.name // Also return name if useful
            };
        } else {
            throw new Error(`Analysis server returned ${response.status}: ${response.statusText}`);
        }
    } catch (error) {
        console.error("Analysis failed:", error);
        throw new Error("Failed to analyze source. Ensure the analysis server is running (npm run server).");
    }
};

export const analyzeCreative = async (creative) => {
    return new Promise((resolve) => {
        setTimeout(() => {
            resolve({
                score: Math.floor(Math.random() * 20) + 80, // 80-99
                predictions: {
                    ctr: (Math.random() * 2 + 1).toFixed(2) + '%',
                    conversionRate: (Math.random() * 1.5 + 0.5).toFixed(2) + '%',
                },
                suggestions: [
                    'Try increasing the contrast of the headline',
                    'Add a more direct call-to-action',
                    'Use a warmer color palette for better engagement'
                ]
            });
        }, 1500);
    });
};
